/**
 * Timeline Yjs Sync Hook
 *
 * Persists timeline state to Yjs document with debouncing.
 * Allows timeline edits to be saved and restored across sessions.
 *
 * Uses a Yjs map structure:
 * - timeline_drafts/{nodeId} -> TimelineState JSON
 *
 * Persists speed and trackIndex fields. Excludes ephemeral clipboard
 * fields (copiedClip, copiedClipType) from Yjs persistence.
 */

import { useEffect, useRef } from 'react'
import * as Y from 'yjs'
import type { TimelineAudioClip, TimelineClip, TimelineState } from './types'
import { computeTimelineDuration, normalizeAudioClips, normalizeVideoClips } from './timelineUtils'
import { createNadDebug } from '@ponpon/api-client'

const restoreLog = createNadDebug('dw:videoeditor-canvas-preview:restore')

interface UseTimelineYjsSyncOptions {
  /** The Yjs document (from useYDoc) */
  ydoc: Y.Doc | null

  /** Whether Yjs is synced */
  synced: boolean

  /** Current timeline state */
  timelineState: TimelineState

  /** Node ID (unique identifier for this timeline) */
  nodeId: string

  /** Callback to restore state from Yjs */
  onRestoreState: (state: TimelineState) => void

  /** Debounce delay in milliseconds (default: 2000) */
  debounceMs?: number

  /** Set of valid video source node IDs (edge-connected). Clips not in this set are pruned on restore. */
  validVideoSourceIds?: Set<string>

  /** Set of valid audio source node IDs (edge-connected). Audio clips not in this set are pruned on restore. */
  validAudioSourceIds?: Set<string>

  /**
   * When true, only the restore branch runs — the debounced save effect is a
   * no-op. Used by the canvas preview, which reads the modal-owned draft but
   * must not write to it.
   */
  readOnly?: boolean
}

export type TimelineDraftValue = string | number | boolean | null | object | undefined
export type TimelineDraftRecord = Record<string, TimelineDraftValue>

function isTimelineClipArray(value: TimelineDraftValue): value is TimelineClip[] {
  return Array.isArray(value)
}

function isTimelineAudioClipArray(value: TimelineDraftValue): value is TimelineAudioClip[] {
  return Array.isArray(value)
}

export function buildPersistableTimelineDraft(timelineState: TimelineState): TimelineDraftRecord {
  return {
    clips: timelineState.clips,
    audioClips: timelineState.audioClips,
    playheadPosition: timelineState.playheadPosition,
    zoomLevel: timelineState.zoomLevel,
    isPlaying: timelineState.isPlaying,
    selectedClipIds: Array.from(timelineState.selectedClipIds),
    totalDuration: timelineState.totalDuration,
    videoTrackCount: timelineState.videoTrackCount,
    audioTrackCount: timelineState.audioTrackCount,
    // RFC-0651: new timeline-wide state fields.
    mutedVideoTracks: Array.from(timelineState.mutedVideoTracks),
    mutedAudioTracks: Array.from(timelineState.mutedAudioTracks),
    magnetEnabled: timelineState.magnetEnabled,
  }
}

export function writeTimelineDraftToYjs(
  ydoc: Y.Doc | null,
  nodeId: string,
  timelineState: TimelineState,
): boolean {
  if (!ydoc || !nodeId) {
    return false
  }

  const timelineDrafts = ydoc.getMap<Y.Map<TimelineDraftValue>>('timeline_drafts')
  const draftMap = new Y.Map<TimelineDraftValue>()
  const persistableState = buildPersistableTimelineDraft(timelineState)

  for (const [key, value] of Object.entries(persistableState)) {
    draftMap.set(key, value)
  }

  timelineDrafts.set(nodeId, draftMap)
  return true
}

/**
 * Sync timeline state to Yjs with debouncing
 */
export function useTimelineYjsSync({
  ydoc,
  synced,
  timelineState,
  nodeId,
  onRestoreState,
  debounceMs = 2000,
  validVideoSourceIds,
  validAudioSourceIds,
  readOnly = false,
}: UseTimelineYjsSyncOptions) {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hasRestoredRef = useRef(false)
  const lastSavedStateRef = useRef<string>('')

  // Restore state from Yjs on mount
  useEffect(() => {
    if (!ydoc || !synced || hasRestoredRef.current) {
      return
    }

    const timelineDrafts = ydoc.getMap<Y.Map<TimelineDraftValue>>('timeline_drafts')
    const draftData = timelineDrafts.get(nodeId)

    if (draftData) {
      try {
        // Convert Yjs Map to plain object
        const draftObj: TimelineDraftRecord = draftData instanceof Y.Map
          ? Object.fromEntries(draftData.entries())
          : {}

        // Parse into TimelineState
        let restoredState = parseTimelineState(draftObj)

        if (restoredState) {
          // Filter restored clips to only include those whose sourceNodeId
          // is still edge-connected. This prevents stale clips from flashing
          // on open when Yjs restoration races with edge-based initialization.
          if (validVideoSourceIds && validVideoSourceIds.size > 0) {
            restoredState = {
              ...restoredState,
              clips: restoredState.clips.filter(c => validVideoSourceIds.has(c.sourceNodeId)),
            }
          }
          if (validAudioSourceIds && validAudioSourceIds.size > 0) {
            restoredState = {
              ...restoredState,
              audioClips: restoredState.audioClips.filter(c => validAudioSourceIds.has(c.sourceNodeId)),
            }
          }
          // Recompute totalDuration after filtering
          restoredState = {
            ...restoredState,
            totalDuration: computeTimelineDuration(restoredState.clips, restoredState.audioClips),
          }

          console.log(`[Timeline Yjs] Restored state for node ${nodeId}`, restoredState)
          restoreLog({
            event: 'restore-state',
            nodeId,
            clipsCount: restoredState.clips.length,
            audioClipsCount: restoredState.audioClips.length,
            totalDuration: Number(restoredState.totalDuration.toFixed(3)),
            readOnly,
          })
          onRestoreState(restoredState)
          lastSavedStateRef.current = JSON.stringify(restoredState)
        }
      } catch (error) {
        console.error('[Timeline Yjs] Failed to restore state:', error)
      }
    }

    hasRestoredRef.current = true
  }, [ydoc, synced, nodeId, onRestoreState, validVideoSourceIds, validAudioSourceIds, readOnly])

  // Debounced save to Yjs
  useEffect(() => {
    if (!ydoc || !synced || !hasRestoredRef.current || readOnly) {
      return
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      const persistableState = buildPersistableTimelineDraft(timelineState)
      const currentStateStr = JSON.stringify(persistableState)

      // Skip if state hasn't changed
      if (currentStateStr === lastSavedStateRef.current) {
        return
      }

      try {
        writeTimelineDraftToYjs(ydoc, nodeId, timelineState)

        lastSavedStateRef.current = currentStateStr
        console.log(`[Timeline Yjs] Saved state for node ${nodeId}`)
      } catch (error) {
        console.error('[Timeline Yjs] Failed to save state:', error)
      }
    }, debounceMs)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [ydoc, synced, timelineState, nodeId, debounceMs, readOnly])
}

/**
 * Parse Yjs data into TimelineState with defaults for new V2 fields
 */
function parseTimelineState(data: TimelineDraftRecord | null): TimelineState | null {
  if (!data || typeof data !== 'object') {
    return null
  }

  const obj = data

  const rawClips = isTimelineClipArray(obj.clips) ? obj.clips : []
  const clips = normalizeVideoClips(
    rawClips.map((clip, index) => ({
      ...clip,
      order: clip.order ?? index,
      startOffset: clip.startOffset,
      trackIndex: clip.trackIndex ?? 0,
      speed: clip.speed ?? 1.0,
      volume: typeof clip.volume === 'number' ? clip.volume : 1.0,
      muted: typeof clip.muted === 'boolean' ? clip.muted : false,
      fadeOutS: typeof clip.fadeOutS === 'number' ? clip.fadeOutS : 0,
    }))
  )

  const rawAudioClips = isTimelineAudioClipArray(obj.audioClips) ? obj.audioClips : []
  const audioClips = normalizeAudioClips(
    rawAudioClips.map((clip) => ({
      ...clip,
      speed: clip.speed ?? 1.0,
      startOffset: clip.startOffset ?? 0,
      trackIndex: clip.trackIndex ?? 0,
    }))
  )

  const derivedVideoTrackCount = Math.max(1, ...clips.map((clip) => clip.trackIndex + 1))
  const derivedAudioTrackCount = Math.max(1, ...audioClips.map((clip) => clip.trackIndex + 1))
  const videoTrackCount = Math.max(
    derivedVideoTrackCount,
    typeof obj.videoTrackCount === 'number' ? obj.videoTrackCount : 1
  )
  const audioTrackCount = Math.max(
    derivedAudioTrackCount,
    typeof obj.audioTrackCount === 'number' ? obj.audioTrackCount : 1
  )

  // Read multi-selection. Backward compat: if a legacy `selectedClipId`
  // (singleton) is present in the persisted draft, lift it into the new set.
  const selectedClipIds = new Set<string>()
  if (Array.isArray(obj.selectedClipIds)) {
    for (const v of obj.selectedClipIds) {
      if (typeof v === 'string') selectedClipIds.add(v)
    }
  } else if (typeof obj.selectedClipId === 'string') {
    selectedClipIds.add(obj.selectedClipId)
  }

  return {
    clips,
    audioClips,
    playheadPosition: typeof obj.playheadPosition === 'number' ? obj.playheadPosition : 0,
    zoomLevel: typeof obj.zoomLevel === 'number' ? obj.zoomLevel : 50,
    isPlaying: typeof obj.isPlaying === 'boolean' ? obj.isPlaying : false,
    selectedClipIds,
    totalDuration: computeTimelineDuration(clips, audioClips),
    videoTrackCount,
    audioTrackCount,
    // Ephemeral fields - always start fresh
    copiedClip: null,
    copiedClipType: null,
    // RFC-0651: rehydrate timeline-wide state. The predicate-form filter
    // narrows the array element type without an `as` cast.
    mutedVideoTracks: new Set<number>(
      Array.isArray(obj.mutedVideoTracks)
        ? obj.mutedVideoTracks.filter((n): n is number => typeof n === 'number')
        : [],
    ),
    mutedAudioTracks: new Set<number>(
      Array.isArray(obj.mutedAudioTracks)
        ? obj.mutedAudioTracks.filter((n): n is number => typeof n === 'number')
        : [],
    ),
    magnetEnabled: typeof obj.magnetEnabled === 'boolean' ? obj.magnetEnabled : true,
  }
}
